/**
 * Coin package form ↔ wire conversion.
 *
 * Price (₹) and coins are INDEPENDENT admin-set values — there is no coin rate
 * between them any more. The price is typed in rupees (2 decimals allowed) and
 * sent as integer `amountPaise`; coins go over the wire untouched.
 *
 * `amountPaise` is the OFFER price actually charged. `originalAmountPaise` is an
 * optional display-only "was" price the app strikes through; empty = no
 * discount. The discount % is computed by the API — this file only previews it.
 */
import { paiseToRupees, rupeesToPaise } from '../../lib/money';

/** The API refuses a price below ₹1 (`amountPaise` ≥ 100). */
export const MIN_PRICE_RUPEES = 1;

export interface AdminPackage {
  id: string;
  name: string;
  coins: number;
  bonusCoins: number;
  totalCoins: number;
  amountPaise: string | number;
  priceLabel: string; // "₹199" — the offer (payable) price
  /** Display-only "was" price; null = no discount. */
  originalAmountPaise: string | null;
  originalPriceLabel: string | null;
  /** Server-derived from the two prices; null = no discount badge. */
  discountPercent: number | null;
  displayOrder: number;
  isPopular: boolean;
  isActive: boolean;
  googlePlayProductId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PkgForm {
  name: string;
  priceRupees: number;
  /** "Was" price in ₹; null = no discount. */
  originalPriceRupees: number | null;
  coins: number;
  bonusCoins: number;
  displayOrder: number;
  isPopular: boolean;
  isActive: boolean;
  googlePlayProductId: string;
}

export interface PkgBody {
  name: string;
  amountPaise: number;
  originalAmountPaise: number | null;
  coins: number;
  bonusCoins: number;
  displayOrder: number;
  isPopular: boolean;
  isActive: boolean;
  googlePlayProductId: string | null;
}

export const EMPTY_PKG_FORM: PkgForm = {
  name: '',
  priceRupees: 99,
  originalPriceRupees: null,
  coins: 1000,
  bonusCoins: 0,
  displayOrder: 0,
  isPopular: false,
  isActive: true,
  googlePlayProductId: '',
};

export function formFromPackage(row: AdminPackage): PkgForm {
  return {
    name: row.name,
    priceRupees: paiseToRupees(row.amountPaise),
    originalPriceRupees:
      row.originalAmountPaise == null ? null : paiseToRupees(row.originalAmountPaise),
    coins: row.coins,
    bonusCoins: row.bonusCoins,
    displayOrder: row.displayOrder,
    isPopular: row.isPopular,
    isActive: row.isActive,
    googlePlayProductId: row.googlePlayProductId ?? '',
  };
}

export function bodyFromForm(form: PkgForm): PkgBody {
  return {
    name: form.name.trim(),
    amountPaise: rupeesToPaise(form.priceRupees),
    // null clears the discount on the server.
    originalAmountPaise:
      form.originalPriceRupees == null ? null : rupeesToPaise(form.originalPriceRupees),
    coins: Math.trunc(form.coins),
    bonusCoins: Math.trunc(form.bonusCoins),
    displayOrder: form.displayOrder,
    isPopular: form.isPopular,
    isActive: form.isActive,
    // Empty → null so the backend clears it (and the UNIQUE index never
    // collides on ""). Required for the package to be buyable via Play Billing.
    googlePlayProductId: form.googlePlayProductId.trim() || null,
  };
}

/** Client-side mirror of the API's validation; null when the form is savable. */
export function packageFormError(form: PkgForm): string | null {
  if (form.name.trim().length < 2) return 'Name needs at least 2 characters';
  if (!(form.priceRupees >= MIN_PRICE_RUPEES)) return 'Price must be at least ₹1';
  if (
    form.originalPriceRupees != null &&
    !(rupeesToPaise(form.originalPriceRupees) > rupeesToPaise(form.priceRupees))
  ) {
    return 'Original price must be higher than the offer price (clear it for no discount)';
  }
  if (!Number.isInteger(form.coins) || form.coins < 1) return 'Paid coins must be a whole number ≥ 1';
  if (!Number.isInteger(form.bonusCoins) || form.bonusCoins < 0)
    return 'Bonus coins must be a whole number ≥ 0';
  return null;
}

/**
 * Informational only: coins the buyer receives (paid + bonus) per ₹1 of price.
 * Null when the price is zero/absent. Nothing is derived from this.
 */
export function coinsPerRupee(
  totalCoins: number,
  amountPaise: string | number | null | undefined,
): number | null {
  const rupees = paiseToRupees(amountPaise);
  if (!(rupees > 0)) return null;
  return totalCoins / rupees;
}

/**
 * PREVIEW ONLY, for the edit modal before saving: the same floored formula the
 * API uses (`packageDiscountPercent`). The table and the app always show the
 * server's `discountPercent`, never this.
 */
export function previewDiscountPercent(
  amountPaise: number,
  originalAmountPaise: number | null,
): number | null {
  if (originalAmountPaise == null || originalAmountPaise <= amountPaise || amountPaise <= 0) {
    return null;
  }
  const pct = Math.floor(((originalAmountPaise - amountPaise) * 100) / originalAmountPaise);
  return pct > 0 ? pct : null;
}
